/**
 * Operator invite form (refreshed).
 *
 * Mounted under ``<RequireSuperAdmin>``. The form covers the operator's
 * core identity + role + tenant grants; on 201 it opens the shared
 * ``TempPasswordModal`` carrying the one-shot temp password from the
 * server (§5.7 — the modal is the source of truth even when email
 * dispatch was requested).
 */
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Plus, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SelectNative } from "@/components/ui/select-native";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/shell/PageHeader";
import { TempPasswordModal } from "@/components/shell/TempPasswordModal";
import {
  asApiError,
  getTenants,
  postOperator,
  type AdminRole,
  type InviteResponse,
  type TenantRow,
} from "@/lib/api";

const ROLE_OPTIONS: ReadonlyArray<{ value: AdminRole; label: string }> = [
  { value: "tenant", label: "Tenant — scoped to selected tenants" },
  { value: "super", label: "Super — full access to every tenant" },
] as const;

export function OperatorNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<AdminRole>("tenant");
  const [tenantIds, setTenantIds] = useState<string[]>([]);
  const [sendEmail, setSendEmail] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invited, setInvited] = useState<InviteResponse | null>(null);

  const tenantsQuery = useQuery({
    queryKey: ["tenants", { for: "operator-invite" }] as const,
    queryFn: () => getTenants({ page_size: 200 }),
  });

  const mutation = useMutation({
    mutationFn: () =>
      postOperator({
        email: email.trim(),
        display_name: displayName.trim() || undefined,
        role,
        tenant_ids: role === "tenant" ? tenantIds : undefined,
        send_email: sendEmail,
      }),
    onSuccess: async (response) => {
      await qc.invalidateQueries({ queryKey: ["admin-users"] });
      setInvited(response);
    },
  });

  useEffect(() => {
    if (role === "super" && tenantIds.length > 0) {
      setTenantIds([]);
    }
  }, [role, tenantIds.length]);

  function toggleTenant(id: string) {
    setTenantIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    if (role === "tenant" && tenantIds.length === 0) {
      setError("Select at least one tenant for a tenant-scoped operator.");
      return;
    }
    mutation.mutate(undefined, {
      onError: (err) => {
        const body = asApiError(err);
        if (body?.error === "duplicate_email") {
          setError("An operator with that email already exists.");
        } else if (body?.error === "tenant_grants_required") {
          setError("Tenant-scoped operators must be granted at least one tenant.");
        } else if (body?.error === "tenant_grants_forbidden_for_super") {
          setError("Super operators cannot be scoped to specific tenants.");
        } else if (body?.error === "invite_rate_limited") {
          setError(
            body.detail ??
              "You have invited too many operators in the last hour. Try again later.",
          );
        } else if (body) {
          setError(body.detail ?? `Couldn't invite operator (${body.error}).`);
        } else {
          setError("Couldn't invite operator. Check your network and try again.");
        }
      },
    });
  }

  function onModalDone() {
    if (invited) {
      const id = invited.operator.id;
      setInvited(null);
      navigate(`/operators/${id}`, { replace: true });
    }
  }

  return (
    <>
      <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3 h-auto px-2 text-muted-foreground">
        <Link to="/operators">
          <ArrowLeft /> Back to operators
        </Link>
      </Button>

      <PageHeader
        icon={UserPlus}
        title="Invite operator"
        description="Mint a new admin portal operator with a one-shot temporary password."
      />

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="text-base">Identity & access</CardTitle>
          <CardDescription>
            Tenant-scoped operators must be granted at least one tenant. Super
            operators see every tenant; their grants are tracked as "all."
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-5" onSubmit={onSubmit} noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={mutation.isPending}
                placeholder="ops@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="display_name">Display name (optional)</Label>
              <Input
                id="display_name"
                value={displayName}
                maxLength={120}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={mutation.isPending}
                placeholder="Alice Wanjiru"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <SelectNative
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value as AdminRole)}
                disabled={mutation.isPending}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </SelectNative>
            </div>

            {role === "tenant" ? (
              <fieldset className="space-y-3 rounded-md border bg-card p-4">
                <legend className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Tenants
                </legend>
                {tenantsQuery.isLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-6 w-3/4" />
                  </div>
                ) : tenantsQuery.error ? (
                  <p className="text-xs text-destructive">
                    Couldn't load tenants. Refresh the page.
                  </p>
                ) : !tenantsQuery.data?.results.length ? (
                  <p className="text-xs text-muted-foreground">
                    No tenants exist yet. Create one before inviting a
                    tenant-scoped operator.
                  </p>
                ) : (
                  <div className="scrollbar-thin max-h-56 space-y-0.5 overflow-y-auto">
                    {tenantsQuery.data.results.map((t: TenantRow) => (
                      <label
                        key={t.id}
                        className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-muted/60"
                      >
                        <input
                          type="checkbox"
                          checked={tenantIds.includes(t.id)}
                          onChange={() => toggleTenant(t.id)}
                          disabled={mutation.isPending}
                          className="h-4 w-4 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring"
                        />
                        <span className="flex-1 text-sm">{t.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {t.environment}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </fieldset>
            ) : null}

            <div className="flex items-start gap-2 rounded-md border bg-muted/30 p-3">
              <input
                id="send_email"
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                disabled={mutation.isPending}
                className="mt-0.5 h-4 w-4 rounded border-input text-primary focus-visible:ring-2 focus-visible:ring-ring"
              />
              <div className="flex-1 space-y-1">
                <Label htmlFor="send_email" className="cursor-pointer">
                  Send invite email with the temporary password
                </Label>
                <p className="text-xs text-muted-foreground">
                  The temporary password is always shown to you here. Email is
                  best-effort; the modal you see next is the source of truth.
                </p>
              </div>
            </div>

            {error ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                {error}
              </div>
            ) : null}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                <Plus />
                {mutation.isPending ? "Inviting…" : "Invite operator"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {invited ? (
        <TempPasswordModal
          title="Operator invited — share this temporary password"
          emailDispatched={invited.email_dispatched}
          sendEmailRequested={sendEmail}
          targetEmail={invited.operator.email}
          tempPassword={invited.temp_password}
          onDone={onModalDone}
        />
      ) : null}
    </>
  );
}

export default OperatorNewPage;
